import { DvaModelBuilder, actionCreatorFactory } from 'dva-model-creator';
import { history } from 'umi';
import {
  getUserInfo,
  login as loginAPI,
  logout as logoutAPI,
  requestOtpLogin as requestOtpLoginAPI,
  verifyOtpLogin as verifyOtpLoginAPI,
  resendOtpLogin as resendOtpLoginAPI,
} from '@/services/user';
import token from '@/utils/token';
import { OtpLoginState, UserModel, UserData } from '@/common/type';
import { get } from 'lodash';

const actionCreator = actionCreatorFactory('user');
const activate = actionCreator<{ user: UserData; jwt: string }>('activate');
const login = actionCreator<{
  identifier: string;
  password: string;
  shop: string;
  remember?: boolean;
  invite?: string;
}>('login');
const requestOtpLogin = actionCreator<{
  identifier: string;
  shop: string;
  remember?: boolean;
}>('requestOtpLogin');
const verifyOtpLogin = actionCreator<{
  session_id: string;
  code: string;
  remember?: boolean;
  invite?: string;
}>('verifyOtpLogin');
const resendOtpLogin = actionCreator<{ session_id: string }>('resendOtpLogin');
const setOtpLogin = actionCreator<OtpLoginState | undefined>('setOtpLogin');
const clearOtpLogin = actionCreator('clearOtpLogin');
const logout = actionCreator('logout');
const clearUser = actionCreator('clearUser');
const fetchCurrent = actionCreator<string | undefined>('fetchCurrent');
const saveData = actionCreator<UserData | undefined>('saveData');
const setErrorMessage = actionCreator<string | undefined>('setErrorMessage');
const setNewMessage = actionCreator<boolean>('setNewMessage');
const setShowTrialAgreement = actionCreator<boolean>('setShowTrialAgreement');

const initState: UserModel = {
  status: undefined,
  data: undefined,
  error: undefined,
  newMessage: false,
  showTrialAgreement: false,
  otpLogin: undefined,
};

function redirect(user?: UserData, params?: string | { [key: string]: string }) {
  const urlParams = new URLSearchParams(window.location.search);
  let redirect = urlParams.get('redirect');
  if (!redirect && user) {
    if (params && typeof params !== 'string' && params.invite) {
      redirect = `/invites?t=${params.invite}`;
    }
    if (redirect && params && typeof params === 'string') {
      redirect = `${redirect}?${params}`;
    }
  }
  history.replace(redirect || `/${params && typeof params === 'string' ? `?${params}` : ''}`);
}

function* completeLogin(
  response: { jwt?: string; user: UserData },
  invite: string | undefined,
  { put }: { put: (action: unknown) => void },
) {
  yield put(clearOtpLogin());
  if (response.jwt) {
    token.save(response.jwt);
  }
  yield put(saveData(response.user));
  yield put(setNewMessage(!!response.user.new_message));
  yield put(setShowTrialAgreement(!!response.user.show_trial_user_agreement));
  redirect(response.user, invite ? { invite } : undefined);
}

function getErrorMessage(response: Record<string, unknown> | undefined) {
  if (!response) {
    return undefined;
  }
  if (get(response, 'data[0].messages[0].message')) {
    return get(response, 'data[0].messages[0].message') as string;
  }
  if (typeof response.message === 'string') {
    return response.message;
  }
  return undefined;
}

export default new DvaModelBuilder(initState, 'user')
  .takeEvery(activate, function*(payload, { call, put }) {
    if (payload.jwt) {
      token.save(payload.jwt);
    }
    yield put(saveData(payload.user));
    redirect(payload.user, 'activated=1');
  })
  .takeEvery(login, function*(payload, { call, put }) {
    if (token.get()) {
      token.remove();
    }
    const { invite, ...loginPayload } = payload;
    let response;
    try {
      response = yield call(loginAPI, loginPayload);
    } catch (error) {
      response = { error };
    }

    if (response?.code === 'OTP_LOGIN_REQUIRED') {
      yield put(setErrorMessage(undefined));
      yield put(
        setOtpLogin({
          identifier: loginPayload.identifier,
          remember: loginPayload.remember,
        }),
      );
      yield put(
        requestOtpLogin({
          identifier: loginPayload.identifier,
          shop: loginPayload.shop,
          remember: loginPayload.remember,
        }),
      );
      return response;
    }

    if (response && response.user) {
      yield* completeLogin(response, invite, { put });
    } else {
      const message = getErrorMessage(response);
      if (message) {
        yield put(setErrorMessage(message));
      } else if (response?.error) {
        yield put(
          setErrorMessage(
            "Oops! There's a problem signing into your account. Please try again later.",
          ),
        );
      }
      yield put(saveData(undefined));
    }
    return response;
  })
  .takeEvery(requestOtpLogin, function*(payload, { call, put, select }) {
    let response;
    try {
      response = yield call(requestOtpLoginAPI, {
        identifier: payload.identifier,
        shop: payload.shop,
      });
    } catch (error) {
      response = { error };
    }

    if (response?.session_id) {
      const current = yield select((state: { user: UserModel }) => state.user.otpLogin);
      yield put(
        setOtpLogin({
          identifier: payload.identifier,
          remember: payload.remember ?? current?.remember,
          session_id: response.session_id,
          email_hint: response.email_hint,
          expires_in: response.expires_in,
        }),
      );
      yield put(setErrorMessage(undefined));
    } else {
      const message = getErrorMessage(response);
      if (message) {
        yield put(setErrorMessage(message));
      }
    }
    return response;
  })
  .takeEvery(verifyOtpLogin, function*(payload, { call, put }) {
    const { invite, ...verifyPayload } = payload;
    let response;
    try {
      response = yield call(verifyOtpLoginAPI, verifyPayload);
    } catch (error) {
      response = { error };
    }

    if (response?.user) {
      yield* completeLogin(response, invite, { put });
    } else {
      const message = getErrorMessage(response) || 'Invalid verification code';
      yield put(setErrorMessage(message));
    }
    return response;
  })
  .takeEvery(resendOtpLogin, function*(payload, { call, put, select }) {
    let response;
    try {
      response = yield call(resendOtpLoginAPI, payload);
    } catch (error) {
      response = { error };
    }

    if (response?.status === 'otp_sent' || response?.email_hint) {
      const current = yield select((state: { user: UserModel }) => state.user.otpLogin);
      if (current) {
        yield put(
          setOtpLogin({
            ...current,
            email_hint: response.email_hint || current.email_hint,
            expires_in: response.expires_in || current.expires_in,
          }),
        );
      }
      yield put(setErrorMessage(undefined));
    } else {
      const message = getErrorMessage(response);
      if (message) {
        yield put(setErrorMessage(message));
      }
    }
    return response;
  })
  .takeEvery(logout, function*(_, { call, put }) {
    const response = yield call(logoutAPI);
    if (response && response.type === 'success') {
      yield put(setErrorMessage(undefined));
      yield put(saveData(undefined));
      yield put(clearOtpLogin());
      history.push({
        pathname: '/login',
      });
    }
  })
  .takeEvery(clearUser, function*(_, { call, put }) {
    const response = yield call(logoutAPI);
    if (response && response.type === 'success') {
      yield put(setErrorMessage(undefined));
      yield put(saveData(undefined));
      yield put(clearOtpLogin());
    }
  })
  .takeEvery(fetchCurrent, function*(redirectOnFailed, { call, put }) {
    let user;
    const response = yield call(getUserInfo, {
      errorHandler: async () => {
        if (redirectOnFailed) {
          history.push({
            pathname: '/login',
            query: {
              redirect: redirectOnFailed,
            },
          });
        }
      },
    });
    if (response && !response.error) {
      user = response;
      const umiLocale = localStorage.getItem('umi_locale');
      user.umiLocale = umiLocale;
    } else if (token.get()) {
      token.remove();
    }
    yield put(saveData(user));
    yield put(setShowTrialAgreement(!!user?.show_trial_user_agreement));
    if (response && response.new_message !== undefined) {
      yield put(setNewMessage(response.new_message));
    }
  })
  .case(saveData, (state, data) => ({
    ...state,
    status: !!data,
    data,
  }))
  .case(setErrorMessage, (state, error) => ({
    ...state,
    error,
  }))
  .case(setNewMessage, (state, newMessage) => ({
    ...state,
    newMessage,
  }))
  .case(setShowTrialAgreement, (state, showTrialAgreement) => ({
    ...state,
    showTrialAgreement,
  }))
  .case(setOtpLogin, (state, otpLogin) => ({
    ...state,
    otpLogin,
    error: undefined,
  }))
  .case(clearOtpLogin, state => ({
    ...state,
    otpLogin: undefined,
  }))
  .build();

export const actions = {
  fetchCurrent,
  login,
  requestOtpLogin,
  verifyOtpLogin,
  resendOtpLogin,
  setOtpLogin,
  clearOtpLogin,
  logout,
  clearUser,
  activate,
  saveData,
  setNewMessage,
  setShowTrialAgreement,
  setErrorMessage,
};
